const DB_NAME = 'ip-pms-offline';
const STORE_NAME = 'offline-queue';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Add item to offline queue
export async function addToQueue(item) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.add({ ...item, timestamp: Date.now() });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Get all queued items
export async function getQueue() {
  const db = await openDB();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve([]);
  });
}

// Remove item from queue
export async function removeFromQueue(id) {
  const db = await openDB();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.delete(id);
    tx.oncomplete = () => resolve();
  });
}

// Clear entire queue
export async function clearQueue() {
  const db = await openDB();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    tx.oncomplete = () => resolve();
  });
}

// Process queue — try to send all pending items
export async function processQueue(apiClient) {
  if (!navigator.onLine) return { processed: 0, remaining: 0 };
  const items = await getQueue();
  let processed = 0;

  for (const item of items) {
    try {
      if (item.type === 'daily_update') {
        await apiClient.post(`/projects/${item.projectId}/daily-updates`, {
          content_text: item.content_text,
          update_date: item.update_date,
        });
      } else if (item.type === 'media_upload') {
        const formData = new FormData();
        // Convert stored base64 back to blob
        const blob = base64ToBlob(item.fileData, item.mimeType);
        formData.append('file', blob, item.fileName);
        formData.append('entity_type', item.entity_type);
        formData.append('entity_id', item.entity_id);
        await apiClient.post('/media/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else if (item.type === 'check_in') {
        await apiClient.post('/attendance/check-in', item.data);
      }
      await removeFromQueue(item.id);
      processed++;
    } catch {
      break; // Stop on first failure (still offline or server error)
    }
  }

  const remaining = (await getQueue()).length;
  return { processed, remaining };
}

// Convert file to base64 for IndexedDB storage
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function base64ToBlob(base64, mimeType) {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mimeType });
}

// Register for background sync
export function registerSync() {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    navigator.serviceWorker.ready.then((reg) => {
      reg.sync.register('sync-offline-queue').catch(() => {});
    });
  }
}

// Listen for sync success messages from SW
export function onSyncSuccess(callback) {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'SYNC_SUCCESS') callback(event.data.item);
    });
  }
}
